import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./DateRangeReportModal.css";
import { useWorkshop } from "../../../contexts/WorkshopContext";

interface DateRangeReportModalProps {
  show: boolean;
  isDarkMode: boolean;
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onClose: () => void;
  onDownload: () => void;
}

export const DateRangeReportModal: React.FC<DateRangeReportModalProps> = ({
  show,
  isDarkMode,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onClose,
  onDownload,
}) => {
  const { selectedWorkshop } = useWorkshop();
  const [inspectionCount, setInspectionCount] = useState<number | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>(""); // "" = all, "open" = open, "closed" = closed

  // State for date range picker
  const [dateRangeState, setDateRangeState] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  // Get today's date
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  // Validate dates
  const isValidRange =
    startDate && endDate && startDate <= endDate && new Date(endDate) <= today;

  // Update internal state when props change from parent (only on modal open)
  useEffect(() => {
    if (show && startDate && endDate) {
      // Parse dates in local timezone to avoid day shift
      const parseLocalDate = (dateStr: string) => {
        const [year, month, day] = dateStr.split("-").map(Number);
        return new Date(year, month - 1, day);
      };

      setDateRangeState([
        {
          startDate: parseLocalDate(startDate),
          endDate: parseLocalDate(endDate),
          key: "selection",
        },
      ]);
    }
  }, [show, startDate, endDate]);

  // Handle date range change
  const handleDateRangeChange = (item: any) => {
    const newRange = item.selection;
    setDateRangeState([newRange]);

    // Convert dates to YYYY-MM-DD format using local timezone
    const formatLocalDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const startDateStr = formatLocalDate(newRange.startDate);
    const endDateStr = formatLocalDate(newRange.endDate);

    onStartDateChange(startDateStr);
    onEndDateChange(endDateStr);
  };

  // Search for inspections when dates are valid
  useEffect(() => {
    if (!isValidRange || !show || !selectedWorkshop) {
      setInspectionCount(null);
      return;
    }

    const searchInspections = async () => {
      setIsSearching(true);
      try {
        // Use selectedWorkshop from context
        const workshopId = selectedWorkshop.id;

        let url = `${
          process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1"
        }/inspections?workshop_id=${workshopId}&start_date=${startDate}&end_date=${endDate}`;

        if (statusFilter) {
          url += `&inspection_status=${statusFilter}`;
        }

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setInspectionCount(data.length);
        } else {
          console.error(
            "[DateRange] Search failed:",
            response.status,
            response.statusText
          );
          setInspectionCount(null);
        }
      } catch (error) {
        console.error("[DateRange] Error searching inspections:", error);
        setInspectionCount(null);
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce search
    const timer = setTimeout(searchInspections, 500);
    return () => clearTimeout(timer);
  }, [startDate, endDate, isValidRange, show, statusFilter, selectedWorkshop]);

  const handlePreview = () => {
    if (!selectedWorkshop) {
      console.error("No workshop selected");
      return;
    }

    setIsGeneratingReport(true);
    let url = `${
      process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1"
    }/inspections/report/range?workshop_id=${
      selectedWorkshop.id
    }&start_date=${startDate}&end_date=${endDate}`;

    if (statusFilter) {
      url += `&inspection_status=${statusFilter}`;
    }

    setPreviewUrl(url);
    setShowPreview(true);
  };

  const handleDownloadClick = () => {
    setIsGeneratingReport(true);
    onDownload();
  };

  return (
    <AnimatePresence mode="wait">
      {show && (
        <motion.div
          key="date-range-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
        >
          <motion.div
            key="date-range-content"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-4xl max-h-[90vh] mx-4 rounded-xl shadow-2xl overflow-hidden flex flex-col ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-phoenix-600 to-phoenix-700 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Inspections Report
                  </h3>
                  <p className="text-phoenix-100 text-sm">Select date range</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5 overflow-y-auto flex-1">
              {/* Date Range Picker */}
              <div
                className={`flex justify-center rounded-lg overflow-hidden ${
                  isDarkMode ? "date-range-dark" : ""
                }`}
              >
                <DateRange
                  ranges={dateRangeState}
                  onChange={handleDateRangeChange}
                  maxDate={today}
                  rangeColors={["#DC5B23"]}
                  color="#DC5B23"
                  moveRangeOnFirstSelection={false}
                  months={2}
                  direction="horizontal"
                  showDateDisplay={false}
                  className={isDarkMode ? "dark-theme" : ""}
                />
              </div>

              {/* Status Filter */}
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Filter by Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={`block w-full py-2 px-3 border rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">All Inspections</option>
                  <option value="open">Open Only</option>
                  <option value="closed">Closed Only</option>
                </select>
              </div>

              {/* Date Range Visual Indicator */}
              {startDate && endDate && isValidRange && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-lg ${
                    isDarkMode
                      ? "bg-green-900/30 border-2 border-green-700/50"
                      : "bg-green-50 border-2 border-green-200"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        isDarkMode ? "bg-green-800" : "bg-green-100"
                      }`}
                    >
                      <svg
                        className={`w-5 h-5 ${
                          isDarkMode ? "text-green-400" : "text-green-600"
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-sm font-semibold ${
                          isDarkMode ? "text-green-300" : "text-green-800"
                        }`}
                      >
                        Selected Range:{" "}
                        {new Date(startDate).toLocaleDateString()} -{" "}
                        {new Date(endDate).toLocaleDateString()}
                      </p>
                      <p
                        className={`text-xs ${
                          isDarkMode ? "text-green-400" : "text-green-600"
                        }`}
                      >
                        {Math.ceil(
                          (new Date(endDate).getTime() -
                            new Date(startDate).getTime()) /
                            (1000 * 60 * 60 * 24)
                        ) + 1}{" "}
                        days
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Inspection Count */}
              {isValidRange && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={`p-5 rounded-lg ${
                    isDarkMode
                      ? "bg-gradient-to-r from-phoenix-900/40 to-phoenix-800/40 border border-phoenix-700/50"
                      : "bg-gradient-to-r from-phoenix-50 to-orange-50 border border-phoenix-200"
                  }`}
                >
                  {isSearching ? (
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-phoenix-600" />
                      <p
                        className={`text-sm font-medium ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Searching inspections...
                      </p>
                    </div>
                  ) : inspectionCount !== null ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-3 rounded-lg ${
                            isDarkMode ? "bg-phoenix-800" : "bg-phoenix-100"
                          }`}
                        >
                          <svg
                            className={`w-6 h-6 ${
                              isDarkMode
                                ? "text-phoenix-400"
                                : "text-phoenix-600"
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p
                            className={`text-2xl font-bold ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {inspectionCount}
                          </p>
                          <p
                            className={`text-sm ${
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            {inspectionCount === 1
                              ? "Inspection found"
                              : "Inspections found"}
                          </p>
                        </div>
                      </div>
                      {inspectionCount > 0 && (
                        <div
                          className={`px-3 py-1 rounded-full ${
                            isDarkMode
                              ? "bg-green-900 text-green-300"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          <span className="text-xs font-semibold">Ready</span>
                        </div>
                      )}
                    </div>
                  ) : null}
                </motion.div>
              )}

              {/* Info Message */}
              <div
                className={`p-4 rounded-lg ${
                  isDarkMode
                    ? "bg-blue-900/30 border border-blue-700/50"
                    : "bg-blue-50 border border-blue-200"
                }`}
              >
                <div className="flex gap-3">
                  <svg
                    className={`w-5 h-5 flex-shrink-0 ${
                      isDarkMode ? "text-blue-400" : "text-blue-600"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-blue-300" : "text-blue-800"
                    }`}
                  >
                    The report will include all inspections created within the
                    selected date range. You cannot select future dates.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div
              className={`px-6 py-4 flex justify-between items-center ${
                isDarkMode ? "bg-gray-900/50" : "bg-gray-50"
              }`}
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className={`px-6 py-2.5 rounded-lg font-semibold transition-colors ${
                  isDarkMode
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-200"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                }`}
              >
                Cancel
              </motion.button>

              <div className="flex gap-3">
                {/* Preview Button */}
                <motion.button
                  whileHover={{
                    scale: !isValidRange || inspectionCount === 0 ? 1 : 1.02,
                  }}
                  whileTap={{
                    scale: !isValidRange || inspectionCount === 0 ? 1 : 0.98,
                  }}
                  onClick={handlePreview}
                  disabled={!isValidRange || inspectionCount === 0}
                  className={`px-6 py-2.5 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                    !isValidRange || inspectionCount === 0
                      ? "bg-gray-400 cursor-not-allowed text-gray-200"
                      : isDarkMode
                      ? "bg-gray-700 hover:bg-gray-600 text-white"
                      : "bg-gray-600 hover:bg-gray-700 text-white"
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  Preview
                </motion.button>

                {/* Download Button */}
                <motion.button
                  whileHover={{
                    scale: !isValidRange || inspectionCount === 0 ? 1 : 1.02,
                  }}
                  whileTap={{
                    scale: !isValidRange || inspectionCount === 0 ? 1 : 0.98,
                  }}
                  onClick={handleDownloadClick}
                  disabled={!isValidRange || inspectionCount === 0}
                  className={`px-6 py-2.5 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                    !isValidRange || inspectionCount === 0
                      ? "bg-gray-400 cursor-not-allowed text-gray-200"
                      : "bg-gradient-to-r from-phoenix-600 to-phoenix-700 hover:from-phoenix-700 hover:to-phoenix-800 text-white shadow-lg"
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Download
                </motion.button>

                {/* Email Button - Coming Soon */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled
                  className={`px-6 py-2.5 rounded-lg font-semibold transition-all flex items-center gap-2 relative ${
                    isDarkMode
                      ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                  title="Coming soon"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  Email
                  <span
                    className={`absolute -top-1 -right-1 px-1.5 py-0.5 text-[9px] rounded-full ${
                      isDarkMode
                        ? "bg-yellow-900 text-yellow-300"
                        : "bg-yellow-200 text-yellow-800"
                    }`}
                  >
                    Soon
                  </span>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <motion.div
          key="preview-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowPreview(false)}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-2"
        >
          <motion.div
            key="preview-content"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-6xl h-full max-h-[95vh] rounded-xl shadow-2xl overflow-hidden flex flex-col ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            {/* Preview Header */}
            <div className="bg-gradient-to-r from-phoenix-600 to-phoenix-700 px-3 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <div>
                  <h3 className="text-sm font-bold text-white">
                    Report Preview - {inspectionCount}{" "}
                    {inspectionCount === 1 ? "inspection" : "inspections"}
                  </h3>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowPreview(false)}
                className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </motion.button>
            </div>

            {/* PDF Preview */}
            <div className="flex-1 relative overflow-hidden bg-gray-100 dark:bg-gray-900">
              {isGeneratingReport && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                  <div
                    className={`text-center p-8 rounded-lg ${
                      isDarkMode ? "bg-gray-800" : "bg-white"
                    } shadow-xl max-w-md mx-4`}
                  >
                    <div className="flex justify-center mb-4">
                      <svg
                        className="animate-spin h-12 w-12 text-[#DC5B23]"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                    <h3
                      className={`text-lg font-semibold mb-2 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Generating Report
                    </h3>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      This may take up to 2 minutes depending on the size of the
                      report.
                      <br />
                      <strong>Please be patient.</strong>
                    </p>
                    <p
                      className={`text-xs mt-3 ${
                        isDarkMode ? "text-gray-500" : "text-gray-500"
                      }`}
                    >
                      Processing {inspectionCount}{" "}
                      {inspectionCount === 1 ? "inspection" : "inspections"}...
                    </p>
                  </div>
                </div>
              )}
              <iframe
                src={`${previewUrl}${
                  localStorage.getItem("access_token")
                    ? `&token=${localStorage.getItem("access_token")}`
                    : ""
                }#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
                className="w-full h-full border-0"
                title="Report Preview"
                onLoad={() => {
                  setIsGeneratingReport(false);
                }}
                onError={(e) => {
                  console.error("Error loading PDF:", e);
                  setIsGeneratingReport(false);
                }}
              />
            </div>

            {/* Preview Footer */}
            <div
              className={`px-3 py-1.5 flex items-center justify-end border-t ${
                isDarkMode
                  ? "bg-gray-900/50 text-gray-400 border-gray-700"
                  : "bg-gray-50 text-gray-600 border-gray-200"
              }`}
            >
              <button
                onClick={handleDownloadClick}
                className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-phoenix-600 to-phoenix-700 hover:from-phoenix-700 hover:to-phoenix-800 text-white text-xs font-semibold transition-all flex items-center gap-1.5 shadow-lg"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
